import * as React from 'react';
import { AnyAction, Dispatch } from 'redux';
import { takeEvery, takeLatest, takeLeading } from 'redux-saga/effects';
import { CreatedUserViewModel, UserViewModel } from 'stageclip-common';

import { ChangePasswordFormData } from 'models/changePasswordFormData';
import { EditUserRequest } from 'api/definitions/editUserRequest';
import { ChangePasswordRequest } from 'api/definitions/changePasswordRequest';
import { CreateUserRequest } from 'api/definitions/createUserRequest';
import { GetAllUsersRequest } from 'api/definitions/getAllUsersRequest';
import { GetUserRequest } from 'api/definitions/getUserRequest';
import * as EntityNormalizers from 'api/normalizers/entityNormalizers';
import * as UserNormalizers from 'api/normalizers/userNormalizers';
import { DataRequestActionTypes, InternalActionTypes } from 'constants/actionTypes';
import { RequestAction } from './allActions';
import { FormSubmitAction } from './formActions';
import { runApiRequest } from './saga/sagaActions';

export interface AllUsersLoadedAction extends AnyAction {
  type: InternalActionTypes.AllUsersLoaded;
  result: EntityNormalizers.NormalizedEntityArray<UserNormalizers.NormalizedUsers>;
}

export interface RequestAllUsersAction extends RequestAction {
  type: DataRequestActionTypes.LoadingAllUsersRequested;
}

export interface RequestCreateUserAction extends FormSubmitAction<UserViewModel> {
  type: DataRequestActionTypes.CreateUserRequested;
}

export interface RequestEditUserAction extends FormSubmitAction<UserViewModel> {
  type: DataRequestActionTypes.EditUserRequested;
  userId: string;
}

export interface CreatedUserAction extends AnyAction {
  result: EntityNormalizers.NormalizedSingleEntity<UserNormalizers.NormalizedUsers<CreatedUserViewModel>>;
  type: InternalActionTypes.UserCreated;
}

export interface EditedUserAction extends AnyAction {
  result: EntityNormalizers.NormalizedSingleEntity<UserNormalizers.NormalizedUsers>;
  type: InternalActionTypes.UserEdited;
}

export interface RequestUserAction extends RequestAction {
  type: DataRequestActionTypes.LoadingUserRequested;
  userId: string;
}

export interface UserLoadedAction extends AnyAction {
  type: InternalActionTypes.UserLoaded;
  result: EntityNormalizers.NormalizedSingleEntity<UserNormalizers.NormalizedUsers>;
}

export interface RequestChangePassword extends FormSubmitAction<ChangePasswordFormData> {
  type: DataRequestActionTypes.ChangePasswordRequested;
  userId: string;
}

export interface PasswordChangedAction extends AnyAction {
  type: InternalActionTypes.PasswordChanged;
  result: EntityNormalizers.NormalizedSingleEntity<UserNormalizers.NormalizedUsers>;
}

function createAllUsersLoadedAction(
  result: EntityNormalizers.NormalizedEntityArray<UserNormalizers.NormalizedUsers>,
): AllUsersLoadedAction {
  return {
    result,
    type: InternalActionTypes.AllUsersLoaded,
  };
}

function createUserLoadedAction(
  result: EntityNormalizers.NormalizedSingleEntity<UserNormalizers.NormalizedUsers>,
): UserLoadedAction {
  return {
    result,
    type: InternalActionTypes.UserLoaded,
  };
}

function createUserCreatedAction(
  result: EntityNormalizers.NormalizedSingleEntity<UserNormalizers.NormalizedUsers<CreatedUserViewModel>>,
): CreatedUserAction {
  return {
    result,
    type: InternalActionTypes.UserCreated,
  };
}

function createUserEditedAction(
  result: EntityNormalizers.NormalizedSingleEntity<UserNormalizers.NormalizedUsers>,
): EditedUserAction {
  return {
    result,
    type: InternalActionTypes.UserEdited,
  };
}

function createPasswordChangedAction(
  result: EntityNormalizers.NormalizedSingleEntity<UserNormalizers.NormalizedUsers>,
): PasswordChangedAction {
  return {
    result,
    type: InternalActionTypes.PasswordChanged,
  };
}

export function createRequestAllUsersAction(component: React.Component, dispatch: Dispatch): RequestAllUsersAction {
  return {
    dispatch,
    requestingComponent: component,
    type: DataRequestActionTypes.LoadingAllUsersRequested,
  };
}

export function createRequestUserAction(
  userId: string,
  component: React.Component,
  dispatch: Dispatch,
): RequestUserAction {
  return {
    dispatch,
    requestingComponent: component,
    type: DataRequestActionTypes.LoadingUserRequested,
    userId,
  };
}

function* loadUsers(action: RequestAllUsersAction) {
  const request = new GetAllUsersRequest(action.requestingComponent, action.dispatch);
  // @ts-ignore
  yield runApiRequest(request, createAllUsersLoadedAction);
}

function* loadUser(action: RequestUserAction) {
  const request = new GetUserRequest(action.userId, action.requestingComponent, action.dispatch);
  // @ts-ignore
  yield runApiRequest(request, createUserLoadedAction);
}

function* createUser(action: RequestCreateUserAction) {
  const request = new CreateUserRequest(action.formData, action.requestingComponent, action.dispatch);
  // @ts-ignore
  yield runApiRequest(request, createUserCreatedAction);
}

function* editUser(action: RequestEditUserAction) {
  const request = new EditUserRequest(action.userId, action.formData, action.requestingComponent, action.dispatch);
  // @ts-ignore
  yield runApiRequest(request, createUserEditedAction);
}

function* changePassword(action: RequestChangePassword) {
  const request = new ChangePasswordRequest(
    action.userId,
    action.formData,
    action.requestingComponent,
    action.dispatch,
  );
  // @ts-ignore
  yield runApiRequest(request, createPasswordChangedAction);
}

export function* loadUsersSaga() {
  yield takeLeading(DataRequestActionTypes.LoadingAllUsersRequested, loadUsers);
}

export function* loadUserSaga() {
  yield takeLatest(DataRequestActionTypes.LoadingUserRequested, loadUser);
}

export function* createUserSaga() {
  yield takeEvery(DataRequestActionTypes.CreateUserRequested, createUser);
}

export function* editUserSaga() {
  yield takeEvery(DataRequestActionTypes.EditUserRequested, editUser);
}

export function* changePasswordSaga() {
  yield takeEvery(DataRequestActionTypes.ChangePasswordRequested, changePassword);
}
